<script context="module">
  import { l } from "$lib/Utils/l10n.svelte";
  import toasts from "$lib/UI/toast-store";
  import reason from "$lib/App/reason-store";
  import { goto } from "$app/navigation";
  import resultsMessages from "$lib/App/results-message-store";

  let prefix = "";

  function substitute(u, params) {
    let r = u;
    for (const property in params) {
      r = r.replace("{" + property + "}", params[property]);
    }
    return r;
  }

  export function url(destination, id, params) {
    const dest = destinations.find((d) => d.id === destination);
    if (!!dest && !!dest[id]) {
      return prefix + substitute(dest[id].uri, params);
    }
    return "";
  }

  export function call(destination, id, params, objectData, addHeaders) {
    const dest = destinations.find((d) => d.id === destination);
    let headers = addHeaders;

    if (!!objectData) {
      headers = {
        ...headers,
        "Content-Type": "application/json",
      };
    }

    if (!!dest && !!dest[id]) {
      return fetch(prefix + substitute(dest[id].uri, params), {
        method: !!dest[id].method ? dest[id].method : "GET",
        body: !!objectData ? JSON.stringify(objectData) : undefined,
        headers: headers,
      })
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              if (destination !== "sessions") {
                reason.set(
                  l("Uw gebruikerssessie is niet geldig. Meld u opnieuw aan."),
                );
                goto("/signedout");
              }
            }
            throw new Error("failed");
          }
          if (dest[id].contentType === "text") {
            return res.text();
          } else if (dest[id].contentType === "none") {
            return res.text();
          } else {
            res.headers.forEach((val, key) => {
              if (key.toLowerCase() === "vzvz-message") {
                resultsMessages.set(val);
              }
            });
            return res.json();
          }
        })
        .catch((error) => {
          const name = !!dest[id] && !!dest[id].name ? dest[id].name : "";
          const errorType =
            !!dest[id] && !!dest[id].errorType ? dest[id].errorType : "log";
          const errorMessage =
            !!dest[id] && !!dest[id].errorMessage
              ? dest[id].errorMessage
              : "Failed";
          switch (errorType) {
            case "none":
              break;
            case "log":
              console.log(name + " : " + errorMessage);
              break;
            default:
              toasts.push(errorMessage);
          }
          throw new Error(errorMessage);
        });
    }
    return Promise.resolve();
  }

  const destinations = [
    {
      id: "postcodes",
      name: l("postcodes"),
      lookup: {
        method: "GET",
        uri: "/api/organizations/postcode/{postcode}/{streetNumber}",
        errorType: "none",
      },
    },
    {
      id: "sessions",
      name: l("Sessie management"),
      upload: {
        method: "GET",
        uri: "/api/sessions/upload",
        contentType: "text",
        errorType: "none",
      },
      authenticated: {
        method: "GET",
        uri: "/api/sessions/authenticated",
        errorType: "none",
      },
      state: {
        method: "GET",
        uri: "/api/sessions/state",
        errorType: "none",
      },
      authorized: {
        method: "GET",
        uri: "/api/sessions/authorized",
        contentType: "none",
        errorType: "none",
      },
      pass: {
        method: "POST",
        uri: "/api/sessions/authorize",
        contentType: "none",
        errorType: "none",
      },
      create: {
        method: "GET",
        uri: "/api/sessions/create",
        errorType: "none",
      },
      open: {
        method: "GET",
        uri: "/api/sessions/open",
        errorType: "none",
      },
      qropen: {
        method: "GET",
        uri: "/api/sessions/qropen",
        errorType: "none",
      },
      close: {
        method: "GET",
        uri: "/api/sessions/close",
        contentType: "none",
        errorType: "none",
      },
    },
    {
      id: "version",
      name: l("Applicatie versie"),
      get: { uri: "/api/locale/version" },
    },
    {
      id: "address-types",
      name: l("Adres types"),
      query: {
        method: "GET",
        uri: "/api/config/address-types",
        errorType: "log",
        errorMessage: "Adres types niet geladen",
      },
    },
    {
      id: "types",
      name: l("Types"),
      query: {
        method: "GET",
        uri: "/api/config/types",
        errorType: "log",
        errorMessage: "Types niet geladen",
      },
    },
    {
      id: "classes",
      name: l("Classificaties"),
      query: {
        method: "GET",
        uri: "/api/config/classes",
        errorType: "log",
        errorMessage: "Classificaties niet geladen",
      },
    },
    {
      id: "author-icons",
      name: l("Auteur pictogrammen"),
      query: {
        method: "GET",
        uri: "/api/config/author-icons",
        errorType: "log",
        errorMessage: "Auteurdefinities en -pictogrammen niet geladen",
      },
    },
    {
      id: "sources",
      name: l("Bron informatie"),
      get: {
        method: "GET",
        uri: "/api/sources",
        errorType: "log",
        errorMessage: "Broninformatie niet geladen",
      },
    },
    {
      id: "gegevensDiensten",
      name: l("Gegevensdiensten"),
      query: {
        method: "GET",
        uri: "/api/config/gegevensdiensten",
        errorType: "log",
        errorMessage: "Gegevensdiensten niet geladen",
      },
    },
    {
      id: "interactions",
      name: l("Interacties"),
      query: {
        method: "GET",
        uri: "/api/config/interactions",
        errorType: "log",
        errorMessage: "Interacties niet geladen",
      },
    },
    {
      id: "name-types",
      name: l("Naam types"),
      query: {
        method: "GET",
        uri: "/api/config/name-types",
        errorType: "log",
        errorMessage: "Naam types niet geladen",
      },
    },
    {
      id: "zorgaanbieder-types",
      name: l("Zorgaanbieder types"),
      query: {
        method: "GET",
        uri: "/api/config/organization-rolecodes",
        errorType: "log",
        errorMessage: "Zorgaanbiedertypes niet geladen",
      },
    },
    {
      id: "toplevel-types",
      name: l("Toplevel Zorgaanbieder types"),
      query: {
        method: "GET",
        uri: "/api/config/toplevel-rolecodes",
        errorType: "log",
        errorMessage: "Toplevel zorgaanbiedertypes niet geladen",
      },
    },

    {
      id: "zorgverlener-types",
      name: l("Zorgaanbieder types"),
      query: {
        method: "GET",
        uri: "/api/config/individual-rolecodes",
        errorType: "log",
        errorMessage: "Zorgaanbiedertypes niet geladen",
      },
    },
    {
      id: "individuals",
      name: l("Zorgverleners"),
      new: {
        method: "POST",
        uri: "/api/individuals",
        errorType: "toast",
        errorMessage: l("Aanleggen zorgverlener niet mogelijk"),
      },
      delete: {
        method: "DELETE",
        uri: "/api/individuals/{id}",
        errorType: "toast",
        errorMessage: l("Verwijderen zorgverlener niet mogelijk"),
        contentType: "none",
      },
      change: {
        method: "PUT",
        uri: "/api/individuals/{id}",
        errorType: "toast",
        errorMessage: l("Wijzigen zorgverlener niet mogelijk"),
      },
      relations: {
        method: "GET",
        uri: "/api/individuals/{id}/relations",
        errorType: "toast",
        errorMessage: l("Geen contactgegevens"),
      },
      identification: {
        method: "GET",
        uri: "/api/individuals/identification/{id}",
        errorType: "toast",
        errorMessage: l("Geen zorgverlener gevonden"),
      },
      search: {
        method: "GET",
        uri: "/api/individuals/search/{searchString}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      name_city: {
        method: "GET",
        uri: "/api/individuals/filter/name-city/{name}/{city}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      name_speciality: {
        method: "GET",
        uri: "/api/individuals/filter/name-speciality/{name}/{speciality}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      speciality_city: {
        method: "GET",
        uri: "/api/individuals/filter/speciality-city/{speciality}/{city}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      speciality_postalcode: {
        method: "GET",
        uri: "/api/individuals/filter/speciality-postalcode/{speciality}/{postalCode}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      postalcode_streetnumber: {
        method: "GET",
        uri: "/api/individuals/filter/postalcode-streetnumber/{postalCode}/{streetNumber}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      get: {
        method: "GET",
        uri: "/api/individuals/{id}",
        errorType: "toast",
        errorMessage: l("Geen zorgverlener gevonden"),
      },
    },
    {
      id: "mandates",
      name: l("Mandaten"),
      get: {
        method: "GET",
        uri: "/api/mandates",
        errorType: "toast",
        errorMessage: l("Geen mandaat gevonden"),
      },
      current: {
        method: "GET",
        uri: "/api/mandates/current",
        errorType: "none",
      },
      create: {
        method: "POST",
        uri: "/api/mandates",
        errorType: "toast",
        errorMessage: l("Geen mandaat aangelegd"),
      },
      activate: {
        method: "POST",
        uri: "/api/mandates/{id}/activate",
        errorType: "toast",
        errorMessage: l("Activering mandaat niet mogelijk"),
      },
      delete: {
        method: "DELETE",
        uri: "/api/mandates/{id}",
        errorType: "toast",
        errorMesssage: l("Afsluiten mandaat niet mogelijk"),
        contentType: "none"
      },
    },
    {
      id: "organizations",
      name: l("Zorgaanbieders"),
      new: {
        method: "POST",
        uri: "/api/organizations",
        errorType: "toast",
        errorMessage: l("Aanleggen afdeling niet mogelijk"),
        contentType: "none",
      },
      suborgids: {
        method: "GET",
        uri: "/api/organizations/{id}/suborgids",
        errorType: "none",
      },
      delete: {
        method: "DELETE",
        uri: "/api/organizations/{id}",
        errorType: "toast",
        errorMessage: l("Verwijderen afdeling niet mogelijk"),
        contentType: "none",
      },
      change: {
        method: "PUT",
        uri: "/api/organizations/{id}",
        errorType: "toast",
        errorMessage: l("Wijzigen organisatie niet mogelijk"),
      },
      search: {
        method: "GET",
        uri: "/api/organizations/search/{searchString}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
      identification: {
        method: "GET",
        uri: "/api/organizations/identification/{id}",
        errorType: "toast",
        errorMessage: l("geen zorgaanbieder gevonden"),
      },
      typedSearch: {
        method: "GET",
        uri: "/api/organizations/typed-search/{selectedType}/{searchString}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
      individuals: {
        method: "GET",
        uri: "/api/organizations/{id}/individuals",
        errorType: "none",
        errorMessage: l("Geen zorgverleners bij deze zorgaanbieder"),
      },
      suborganizations: {
        method: "GET",
        uri: "/api/organizations/{id}/suborganizations",
        errorType: "none",
        errorMessage: l("Geen afdelingen bij deze zorgaanbieder"),
      },
      get: {
        method: "GET",
        uri: "/api/organizations/{id}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieder gevonden"),
      },
      unfiltered: {
        method: "GET",
        uri: "/api/organizations/unfiltered/{id}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieder gevonden"),
      },
      name_city: {
        method: "GET",
        uri: "/api/organizations/filter/name-city/{name}/{city}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
      name_speciality: {
        method: "GET",
        uri: "/api/organizations/filter/name-speciality/{name}/{speciality}",
        errorType: "toast",
        errorMessage: l("Geen zorgverleners gevonden"),
      },
      speciality_city: {
        method: "GET",
        uri: "/api/organizations/filter/speciality-city/{speciality}/{city}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
      speciality_postalcode: {
        method: "GET",
        uri: "/api/organizations/filter/speciality-postalcode/{speciality}/{postalCode}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
      postalcode_streetnumber: {
        method: "GET",
        uri: "/api/organizations/filter/postalcode-streetnumber/{postalCode}/{streetNumber}",
        errorType: "toast",
        errorMessage: l("Geen zorgaanbieders gevonden"),
      },
    },
  ];
</script>
